import {
    IconAlertCircle,
    IconAlertTriangle,
    IconBolt,
    IconChartBar,
    IconCheck,
    IconDownload,
    IconRefresh,
    IconRobot,
    IconSettings,
    IconTrash,
    IconTrendingUp,
    IconX
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GHL } from '../ghl';
import { debug } from '../utils/debug';

export default function Dashboard() {
  const [searchParams] = useSearchParams();
  const urlLocationId = searchParams.get('location_id');
  const isDebugMode = process.env.REACT_APP_DEBUG === 'true';
  const [alert, setAlert] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const showAlert = (message, type = 'info') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  debug.log('[Dashboard] Component mounted');
  debug.log('[Dashboard] URL location_id param:', urlLocationId);
  debug.log('[Dashboard] All search params:', Object.fromEntries(searchParams));

  // Detect GHL template variable (not interpolated) and treat as empty
  const isTemplateVariable = urlLocationId === '{{location.id}}' || urlLocationId?.includes('{{');
  const validUrlLocationId = isTemplateVariable ? null : urlLocationId;

  if (isTemplateVariable) {
    debug.log('[Dashboard] ⚠️ Detected un-interpolated GHL template variable, will use SSO');
    // Clear localStorage since it has a bad value
    localStorage.removeItem('ghl_location_id');
  }

  // Get location_id from URL or localStorage - persists across sessions
  const [locationId, setLocationId] = useState(() => {
    const fromUrl = validUrlLocationId;
    const fromStorage = localStorage.getItem('ghl_location_id');

    // Also validate localStorage value isn't a template variable
    const isStorageTemplateVariable = fromStorage === '{{location.id}}' || fromStorage?.includes('{{');
    const validFromStorage = isStorageTemplateVariable ? null : fromStorage;

    debug.log('[Dashboard] Initial state: fromUrl=', fromUrl, 'fromStorage=', fromStorage, 'isStorageTemplateVariable=', isStorageTemplateVariable);
    return fromUrl || validFromStorage;
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [workerStatus, setWorkerStatus] = useState(null);
  const [recentSpam, setRecentSpam] = useState([]);
  const [spamPage, setSpamPage] = useState(1);
  const [spamPerPage] = useState(10);
  const [totalSpamCount, setTotalSpamCount] = useState(0);
  const [locationResolved, setLocationResolved] = useState(false);
  const [trainingDialog, setTrainingDialog] = useState(null); // { conversation_id, spam_data }

  // Track collapsed categories (persisted in localStorage)
  const [collapsedCategories, setCollapsedCategories] = useState(() => {
    try {
      const stored = localStorage.getItem('collapsed_spam_categories');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    // Store location_id in localStorage when it's available in URL
    debug.log('[Dashboard] First effect - validUrlLocationId changed:', validUrlLocationId);
    if (validUrlLocationId) {
      debug.log('[Dashboard] Storing to localStorage:', validUrlLocationId);
      localStorage.setItem('ghl_location_id', validUrlLocationId);
      setLocationId(validUrlLocationId);
    }
  }, [validUrlLocationId]);

  // Unified effect to resolve location_id and fetch dashboard data
  useEffect(() => {
    debug.log('[Dashboard] Main effect starting - locationResolved:', locationResolved);
    debug.log('[Dashboard] Current locationId state:', locationId);

    const resolveLocationAndFetchData = async () => {
      try {
        let resolvedLocationId = locationId;
        debug.log('[Dashboard] Starting resolution with locationId:', resolvedLocationId);

        // If no location_id yet, try SSO
        if (!resolvedLocationId) {
          debug.log('[Dashboard] No location_id found, attempting SSO...');
          const ghl = new GHL();
          const userData = await ghl.getUserData();

          debug.log('[Dashboard] SSO userData received:', userData);

          const locationFromSSO = userData?.activeLocation || userData?.locationId;
          if (userData && locationFromSSO) {
            debug.log('[Dashboard] ✅ Got location_id from SSO:', locationFromSSO);
            localStorage.setItem('ghl_location_id', locationFromSSO);
            resolvedLocationId = locationFromSSO;
            setLocationId(resolvedLocationId);
          } else {
            debug.log('[Dashboard] ❌ SSO failed - userData:', userData, 'has activeLocation?', userData?.activeLocation, 'has locationId?', userData?.locationId);
            setError('Unable to determine location. Please access this page from within the platform.');
            setLoading(false);
            setLocationResolved(true);
            return;
          }
        }

        // Now fetch dashboard data with resolved location_id
        debug.log('[Dashboard] ✅ Final resolved location_id:', resolvedLocationId);
        debug.log('Dashboard location_id:', resolvedLocationId);
        debug.log('Fetching dashboard data for location:', resolvedLocationId);

        const statsResponse = await fetch(`/api/dashboard/stats/${resolvedLocationId}`);
        debug.log('[Dashboard] Stats API response status:', statsResponse.status);
        debug.log('[Dashboard] Stats API URL:', `/api/dashboard/stats/${resolvedLocationId}`);

        if (!statsResponse.ok) {
          throw new Error(`Dashboard API returned ${statsResponse.status}`);
        }
        const dashboardData = await statsResponse.json();
        debug.log('[Dashboard] Dashboard data received successfully:', dashboardData);

        // Set stats from comprehensive endpoint
        setStats({
          totalSpam: dashboardData.spam_stats.total_spam,
          highConfidence: dashboardData.spam_stats.high_confidence,
          mediumConfidence: dashboardData.spam_stats.medium_confidence,
          lowConfidence: dashboardData.spam_stats.low_confidence,
          avgConfidence: (dashboardData.spam_stats.avg_confidence * 100).toFixed(1),
          lastScan: dashboardData.sync_status.last_scan,
          nextScan: dashboardData.sync_status.next_scan,
          totalScanned: dashboardData.sync_status.total_scanned,
          spamRate: dashboardData.spam_stats.spam_rate.toFixed(1),
          taggedCount: dashboardData.spam_stats.tagged_count,
          archivedCount: dashboardData.spam_stats.archived_count,
          downloadedCount: dashboardData.spam_stats.downloaded_count,
          deletedCount: dashboardData.spam_stats.deleted_count,
          markedForDeletionCount: dashboardData.spam_stats.marked_for_deletion_count,
          sentinelConfigured: dashboardData.sentinel_configured,
          trends: dashboardData.trends,
          topCategories: dashboardData.top_categories,
          subscription: dashboardData.subscription
        });
        debug.log('[Dashboard] Stats state updated');

        setWorkerStatus({
          running: dashboardData.worker_status.active,
          last_run: dashboardData.worker_status.last_heartbeat,
          total_locations: dashboardData.worker_status.total_locations_processed,
          total_emails: dashboardData.worker_status.total_emails_scanned,
          total_spam: dashboardData.worker_status.total_spam_detected
        });
        debug.log('[Dashboard] Worker status state updated');

        // Fetch recent spam
        const offset = (spamPage - 1) * spamPerPage;
        const spamResponse = await fetch(`/api/dashboard/recent-spam/${resolvedLocationId}?limit=${spamPerPage}&offset=${offset}`);
        if (spamResponse.ok) {
          const spamData = await spamResponse.json();
          setRecentSpam(spamData.spam || []);
          setTotalSpamCount(spamData.total || 0);
          debug.log('[Dashboard] Recent spam fetched:', spamData.spam?.length || 0, 'items');
        } else {
          debug.log('[Dashboard] Recent spam fetch failed:', spamResponse.status);
        }

        setError(null);
        debug.log('[Dashboard] ✅ All data loaded successfully');
      } catch (err) {
        debug.error('[Dashboard] ❌ Dashboard fetch error:', err);
        setError(`Failed to load dashboard data: ${err.message}`);
      } finally {
        setLoading(false);
        setLocationResolved(true);
        debug.log('[Dashboard] Data loading complete, showing content');
      }
    };

    // Only resolve on mount
    if (!locationResolved) {
      debug.log('[Dashboard] Calling resolveLocationAndFetchData');
      resolveLocationAndFetchData();
    } else {
      debug.log('[Dashboard] Location already resolved, skipping');
    }
  }, []);

  // Refetch spam when page changes
  useEffect(() => {
    const fetchSpamPage = async () => {
      if (!locationId || !locationResolved) return;

      try {
        const offset = (spamPage - 1) * spamPerPage;
        const spamResponse = await fetch(`/api/dashboard/recent-spam/${locationId}?limit=${spamPerPage}&offset=${offset}`);
        if (spamResponse.ok) {
          const spamData = await spamResponse.json();
          setRecentSpam(spamData.spam || []);
          setTotalSpamCount(spamData.total || 0);
        }
      } catch (err) {
        console.error('Error fetching spam page:', err);
      }
    };

    fetchSpamPage();
  }, [spamPage, locationId, locationResolved, spamPerPage]);

  // Save collapsed state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('collapsed_spam_categories', JSON.stringify(collapsedCategories));
  }, [collapsedCategories]);

  const toggleCategory = (category) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const refreshDashboardData = async () => {
    if (!locationId) {
      debug.log('[Dashboard] Cannot refresh without locationId');
      return;
    }

    try {
      setLoading(true);
      debug.log('[Dashboard] Manually refreshing dashboard data for:', locationId);

      const statsResponse = await fetch(`/api/dashboard/stats/${locationId}`);
      if (!statsResponse.ok) {
        throw new Error(`Dashboard API returned ${statsResponse.status}`);
      }

      const dashboardData = await statsResponse.json();
      console.log('Dashboard API Response:', dashboardData.spam_stats);
      setStats({
        totalSpam: dashboardData.spam_stats.total_spam,
        highConfidence: dashboardData.spam_stats.high_confidence,
        mediumConfidence: dashboardData.spam_stats.medium_confidence,
        lowConfidence: dashboardData.spam_stats.low_confidence,
        avgConfidence: (dashboardData.spam_stats.avg_confidence * 100).toFixed(1),
        lastScan: dashboardData.sync_status.last_scan,
        nextScan: dashboardData.sync_status.next_scan,
        totalScanned: dashboardData.sync_status.total_scanned,
        spamRate: dashboardData.spam_stats.spam_rate.toFixed(1),
        taggedCount: dashboardData.spam_stats.tagged_count,
        archivedCount: dashboardData.spam_stats.archived_count,
        downloadedCount: dashboardData.spam_stats.downloaded_count,
        deletedCount: dashboardData.spam_stats.deleted_count,
        markedForDeletionCount: dashboardData.spam_stats.marked_for_deletion_count,
        sentinelConfigured: dashboardData.sentinel_configured,
        trends: dashboardData.trends,
        topCategories: dashboardData.top_categories
      });

      setWorkerStatus({
        running: dashboardData.worker_status.active,
        last_run: dashboardData.worker_status.last_heartbeat,
        total_locations: dashboardData.worker_status.total_locations_processed,
        total_emails: dashboardData.worker_status.total_emails_scanned,
        total_spam: dashboardData.worker_status.total_spam_detected
      });

      const offset = (spamPage - 1) * spamPerPage;
      const spamResponse = await fetch(`/api/dashboard/recent-spam/${locationId}?limit=${spamPerPage}&offset=${offset}`);
      if (spamResponse.ok) {
        const spamData = await spamResponse.json();
        setRecentSpam(spamData.spam || []);
        setTotalSpamCount(spamData.total || 0);
      }

      setError(null);
      debug.log('[Dashboard] ✅ Data refreshed successfully');
    } catch (err) {
      debug.error('[Dashboard] ❌ Refresh error:', err);
      setError(`Failed to refresh dashboard data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
        <p className="mt-4">Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/20 border border-red-500 rounded-lg p-6">
        <h2 className="text-2xl font-bold mb-2">⚠️ Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Styled Alert */}
      {alert && (
        <div className={`rounded-lg p-4 border transition-all ${
          alert.type === 'error'
            ? 'bg-red-500/20 border-red-500 text-red-200'
            : alert.type === 'warning'
            ? 'bg-yellow-500/20 border-yellow-500 text-yellow-200'
            : 'bg-green-500/20 border-green-500 text-green-200'
        }`}>
          <p className="font-medium">{alert.message}</p>
        </div>
      )}

      {/* Delete Confirmation Message */}
      {confirmDelete && (
        <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg p-4 transition-all">
          <p className="font-bold text-yellow-200 mb-2">⚠️ Confirm Deletion</p>
          <p className="text-yellow-200 text-sm mb-3">
            <strong>RULE:</strong> Only downloaded emails can be marked.<br/>
            This is step 2 of 2 - they'll be deleted on next worker scan.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setConfirmDelete(false)}
              className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white font-medium transition-colors"
            >
              Cancel
            </button>
            <p className="text-yellow-200 text-sm py-2">Click the "⚠️ Click Again to Confirm Delete" button below to proceed</p>
          </div>
        </div>
      )}

      {/* Debug Panel - Shows all data sources (only if REACT_APP_DEBUG=true) */}
      {isDebugMode && (
        <details className="bg-purple-500/10 border border-purple-400/30 rounded-lg p-4 font-mono text-xs">
          <summary className="cursor-pointer text-purple-300 hover:text-purple-200">🔍 Debug Info (click to expand)</summary>
          <div className="mt-3 space-y-2 text-purple-200">
            <div><strong>URL Param (location_id):</strong> {urlLocationId || 'Not set'}</div>
            <div><strong>Is Template Variable:</strong> {isTemplateVariable ? '✅ Yes' : '❌ No'}</div>
            <div><strong>Valid URL Location ID:</strong> {validUrlLocationId || 'Not set'}</div>
            <div><strong>State locationId:</strong> {locationId || 'Not set'}</div>
            <div><strong>localStorage (ghl_location_id):</strong> {localStorage.getItem('ghl_location_id') || 'Not set'}</div>
            <div><strong>In iframe:</strong> {window.self === window.parent ? '❌ No (top level)' : '✅ Yes'}</div>
            <div><strong>window.parent available:</strong> {window.parent ? '✅ Yes' : '❌ No'}</div>
          </div>
        </details>
      )}

      <div>
        <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
          <IconChartBar size={40} />
          Dashboard
        </h1>
        <p className="text-lg opacity-80">Spam detection overview for your location</p>
        <p className="text-sm opacity-60 mt-2">
          Location ID: {locationId || 'Not set'}
          {stats?.subscription && (
            <span className="ml-4">
              {stats.subscription.is_billing_exempt ? (
                <span className="text-blue-400 font-semibold">• Owner Access</span>
              ) : stats.subscription.is_trial ? (
                <span className="text-yellow-400 font-semibold">
                  • Trial ({stats.subscription.days_remaining} {stats.subscription.days_remaining === 1 ? 'day' : 'days'} remaining)
                </span>
              ) : stats.subscription.is_paid ? (
                <span className="text-green-400 font-semibold">• Pro Plan</span>
              ) : (
                <span className="text-red-400 font-semibold">• Trial Expired</span>
              )}
            </span>
          )}
        </p>
      </div>

      {/* Worker Status */}
      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <IconRobot size={28} />
          Worker Status
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm opacity-70">Status</p>
            <p className="text-xl font-bold flex items-center gap-2">
              {workerStatus?.running ? (
                <><IconCheck size={24} className="text-green-400" /> Running</>
              ) : (
                <><IconX size={24} className="text-red-400" /> Stopped</>
              )}
            </p>
          </div>
          <div>
            <p className="text-sm opacity-70">Last Heartbeat</p>
            <p className="text-xl font-bold">
              {workerStatus?.last_run ? new Date(workerStatus.last_run).toLocaleString() : 'Never'}
            </p>
          </div>
          <div>
            <p className="text-sm opacity-70">Locations Processed</p>
            <p className="text-xl font-bold">{workerStatus?.total_locations || 0}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <p className="text-sm opacity-70">Total Emails Scanned</p>
            <p className="text-2xl font-bold">{workerStatus?.total_emails?.toLocaleString() || 0}</p>
          </div>
          <div>
            <p className="text-sm opacity-70">Total Spam Detected</p>
            <p className="text-2xl font-bold text-red-400">{workerStatus?.total_spam?.toLocaleString() || 0}</p>
          </div>
        </div>
      </div>

      {/* Statistics */}
      {stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <p className="text-sm opacity-70 mb-2">Total Spam</p>
              <p className="text-3xl font-bold text-red-400">{stats.totalSpam}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <p className="text-sm opacity-70 mb-2">High Confidence</p>
              <p className="text-3xl font-bold text-red-500">{stats.highConfidence}</p>
              <p className="text-xs opacity-60">≥85%</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <p className="text-sm opacity-70 mb-2">Medium</p>
              <p className="text-3xl font-bold text-orange-400">{stats.mediumConfidence}</p>
              <p className="text-xs opacity-60">70-85%</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <p className="text-sm opacity-70 mb-2">Low</p>
              <p className="text-3xl font-bold text-yellow-400">{stats.lowConfidence}</p>
              <p className="text-xs opacity-60">&lt;70%</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <p className="text-sm opacity-70 mb-2">Avg Confidence</p>
              <p className="text-3xl font-bold">{stats.avgConfidence}%</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <p className="text-sm opacity-70 mb-2">Spam Rate</p>
              <p className="text-3xl font-bold">{stats.spamRate}%</p>
            </div>
          </div>

          {/* Spam Actions Stats */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <IconBolt size={28} />
              Actions Taken
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <p className="text-sm opacity-70 mb-2">Tagged</p>
                <p className="text-3xl font-bold text-yellow-400">{stats.taggedCount || 0}</p>
              </div>
              <div>
                <p className="text-sm opacity-70 mb-2">Archived</p>
                <p className="text-3xl font-bold text-blue-400">{stats.archivedCount || 0}</p>
              </div>
              <div>
                <p className="text-sm opacity-70 mb-2">Downloaded</p>
                <p className="text-3xl font-bold text-green-400">{stats.downloadedCount || 0}</p>
              </div>
              <div>
                <p className="text-sm opacity-70 mb-2 flex items-center gap-1">
                  <IconTrash size={18} />
                  Deleted
                </p>
                <p className="text-3xl font-bold text-red-400">{stats.deletedCount || 0}</p>
              </div>
              <div>
                <p className="text-sm opacity-70 mb-2">Marked for Deletion</p>
                <p className="text-3xl font-bold text-orange-400">{stats.markedForDeletionCount || 0}</p>
              </div>
            </div>
          </div>

          {/* Scan Status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <p className="text-sm opacity-70 mb-2">Total Scanned</p>
              <p className="text-2xl font-bold">{stats.totalScanned?.toLocaleString() || 0}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <p className="text-sm opacity-70 mb-2">Last Scan</p>
              <p className="text-lg font-bold">
                {stats.lastScan && stats.lastScan !== 'Never'
                  ? new Date(stats.lastScan).toLocaleString()
                  : 'Never'}
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <p className="text-sm opacity-70 mb-2">Next Scan</p>
              <p className="text-lg font-bold">
                {stats.nextScan
                  ? new Date(stats.nextScan).toLocaleString()
                  : 'Pending'}
              </p>
            </div>
          </div>

          {/* Top Spam Categories */}
          {stats.topCategories && stats.topCategories.length > 0 && (
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <IconChartBar size={28} />
                Top Spam Categories
              </h2>
              <div className="space-y-3">
                {stats.topCategories.map((cat, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className="text-lg">{cat.category}</span>
                    <span className="bg-red-500/30 px-3 py-1 rounded-full font-bold">{cat.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 7-Day Trend */}
          {stats.trends && stats.trends.length > 0 && (
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <IconTrendingUp size={28} />
                Spam Trend (Last 7 Days)
              </h2>
              <div className="space-y-2">
                {stats.trends.map((trend, idx) => (
                  <div key={idx} className="flex justify-between items-center border-b border-white/10 pb-2">
                    <span className="opacity-80">{new Date(trend.date).toLocaleDateString()}</span>
                    <div className="flex gap-4">
                      <span className="font-bold text-red-400">{trend.spam_count} spam</span>
                      <span className="opacity-70">{(trend.avg_confidence * 100).toFixed(0)}% confidence</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sentinel Status */}
          <div className={`rounded-lg p-6 border ${stats.sentinelConfigured ? 'bg-green-500/20 border-green-500' : 'bg-yellow-500/20 border-yellow-500'}`}>
            <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
              {stats.sentinelConfigured ? (
                <><IconCheck size={28} className="text-green-400" /> Sentinel User Configured</>
              ) : (
                <><IconAlertTriangle size={28} className="text-yellow-400" /> Sentinel User Not Configured</>
              )}
            </h2>
            <p className="opacity-80">
              {stats.sentinelConfigured
                ? 'Spam is automatically assigned to Sentinel user for quarantine.'
                : 'Spam detection running, but auto-assignment disabled. Reinstall app to enable.'}
            </p>
          </div>
        </>
      )}

      {/* Recent Spam */}
      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <IconAlertCircle size={28} className="text-red-400" />
            Recent Spam Detected
          </h2>
          {totalSpamCount > 0 && (
            <span className="text-sm opacity-70">
              {totalSpamCount} total
            </span>
          )}
        </div>
        {recentSpam.length === 0 ? (
          <p className="text-center py-8 opacity-70">No spam detected yet</p>
        ) : (
          <div className="space-y-4">
            {/* Group spam by category */}
            {(() => {
              // Group spam emails by their first category
              const grouped = recentSpam.reduce((acc, spam) => {
                const category = spam.categories && spam.categories.length > 0
                  ? spam.categories[0]
                  : 'Uncategorized';
                if (!acc[category]) acc[category] = [];
                acc[category].push(spam);
                return acc;
              }, {});

              // Sort categories by count (descending)
              const sortedCategories = Object.keys(grouped).sort((a, b) =>
                grouped[b].length - grouped[a].length
              );

              return sortedCategories.map(category => {
                const spamInCategory = grouped[category];
                const isCollapsed = collapsedCategories[category] || false;

                return (
                  <div key={category} className="bg-black/20 rounded border border-white/10">
                    {/* Category Header (Collapsible) */}
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full flex justify-between items-center p-4 hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">
                          {isCollapsed ? '▶' : '▼'}
                        </span>
                        <div className="text-left">
                          <h3 className="font-bold text-lg">{category}</h3>
                          <p className="text-sm opacity-70">
                            {spamInCategory.length} email{spamInCategory.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-red-500/30 px-3 py-1 rounded-full">
                          {(spamInCategory.reduce((sum, s) => sum + s.confidence, 0) / spamInCategory.length * 100).toFixed(0)}% avg
                        </span>
                      </div>
                    </button>

                    {/* Category Content (Collapsible) */}
                    {!isCollapsed && (
                      <div className="px-4 pb-4 space-y-3 border-t border-white/10 pt-3">
                        {spamInCategory.map((spam, idx) => (
                          <div key={idx} className="bg-black/30 rounded p-4 border border-white/5">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <p className="font-bold">{spam.sender || 'Unknown'}</p>
                                <p className="text-sm opacity-70">{spam.subject || '(No subject)'}</p>
                                {spam.deleted && (
                                  <p className="text-xs text-yellow-400 mt-1">
                                    🗑️ Deleted {spam.deleted_at ? new Date(spam.deleted_at).toLocaleDateString() : ''}
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-red-400">{(spam.confidence * 100).toFixed(0)}%</p>
                                <p className="text-xs opacity-60">
                                  {spam.detected_at ? new Date(spam.detected_at).toLocaleDateString() : 'Unknown'}
                                </p>
                              </div>
                            </div>
                            <p className="text-sm opacity-80 line-clamp-2">{spam.body_preview || spam.reasoning}</p>
                            {spam.categories && spam.categories.length > 1 && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {spam.categories.slice(1, 3).map((cat, i) => (
                                  <span key={i} className="text-xs bg-red-500/20 px-2 py-1 rounded">
                                    {cat}
                                  </span>
                                ))}
                              </div>
                            )}
                            <div className="mt-3 flex justify-end">
                              <button
                                onClick={() => setTrainingDialog({ conversation_id: spam.conversation_id, spam_data: spam })}
                                disabled={spam.deleted}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                                  spam.deleted
                                    ? 'bg-gray-500 cursor-not-allowed opacity-50'
                                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                                }`}
                              >
                                <IconRobot size={16} />
                                Train AI
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              });
            })()}


            {/* Pagination Controls */}
            {totalSpamCount > spamPerPage && (
              <div className="mt-6 flex justify-between items-center">
                <button
                  onClick={() => setSpamPage(p => Math.max(1, p - 1))}
                  disabled={spamPage === 1}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    spamPage === 1
                      ? 'bg-gray-600 cursor-not-allowed opacity-50'
                      : 'bg-blue-500 hover:bg-blue-600'
                  }`}
                >
                  ← Previous
                </button>
                <span className="text-sm opacity-70">
                  Page {spamPage} of {Math.ceil(totalSpamCount / spamPerPage)}
                </span>
                <button
                  onClick={() => setSpamPage(p => Math.min(Math.ceil(totalSpamCount / spamPerPage), p + 1))}
                  disabled={spamPage >= Math.ceil(totalSpamCount / spamPerPage)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    spamPage >= Math.ceil(totalSpamCount / spamPerPage)
                      ? 'bg-gray-600 cursor-not-allowed opacity-50'
                      : 'bg-blue-500 hover:bg-blue-600'
                  }`}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <IconBolt size={28} />
          Quick Actions
        </h2>
        <p className="text-white/70 mb-6 text-sm">
          Manage your spam detection queue with these quick actions. Export spam for backup before deletion, or mark conversations for automatic removal.
        </p>
        <div className="flex flex-wrap gap-4">
          <div className="flex flex-col gap-2">
            <button
              onClick={refreshDashboardData}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <IconRefresh size={20} />
              Refresh Data
            </button>
            <p className="text-sm text-white/80 max-w-xs">
              Reload the dashboard statistics and spam queue from the Sentinel server
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={async () => {
                try {
                  const response = await fetch(`/api/spam/${locationId}/export?format=markdown`);
                  if (!response.ok) throw new Error('Export failed');
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;

                  // Get filename from Content-Disposition header if available
                  const contentDisposition = response.headers.get('content-disposition');
                  let filename = 'InboxSentinel_Spam_Emails_Backup.zip';
                  if (contentDisposition) {
                    const match = contentDisposition.match(/filename="([^"]+)"/);
                    if (match) filename = match[1];
                  }

                  a.download = filename;
                  document.body.appendChild(a);
                  a.click();
                  window.URL.revokeObjectURL(url);
                  document.body.removeChild(a);
                  showAlert('✅ Spam exported! All links have been sanitized (http/https → site:). You can now mark for deletion.', 'success');
                  refreshDashboardData();
                } catch (err) {
                  showAlert(`Export failed: ${err.message}`, 'error');
                }
              }}
              className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <IconDownload size={20} />
              Download Spam (Markdown)
            </button>
            <p className="text-xs text-white/60 max-w-xs">
              Export all detected spam as markdown files in a ZIP archive. Links are sanitized for safety. <strong>Do this before deletion!</strong>
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={async () => {
                if (!confirmDelete) {
                  setConfirmDelete(true);
                  return;
                }

                setConfirmDelete(false);
                try {
                  const listRes = await fetch(`/api/spam/${locationId}/list?include_deleted=false&limit=1000`);
                  if (!listRes.ok) throw new Error('Failed to list spam');
                  const listData = await listRes.json();
                  const ids = listData.emails.map(e => e.conversation_id);

                  if (ids.length === 0) {
                    showAlert('No spam to mark for deletion', 'warning');
                    return;
                  }

                  const markRes = await fetch(`/api/spam/${locationId}/mark-for-deletion`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ conversation_ids: ids, mark: true })
                  });

                  if (!markRes.ok) {
                    const err = await markRes.json();
                    throw new Error(err.detail || 'Failed to mark for deletion');
                  }
                  const result = await markRes.json();
                  showAlert(`Marked ${result.marked_count} conversation(s) for deletion. They will be deleted on next worker scan.`, 'success');
                  refreshDashboardData();
                } catch (err) {
                  showAlert(`Failed to mark for deletion: ${err.message}`, 'error');
                }
              }}
              className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                confirmDelete
                  ? 'bg-red-600 hover:bg-red-700 text-white animate-pulse'
                  : 'bg-orange-500 hover:bg-orange-600 text-white'
              }`}
            >
              {confirmDelete ? (
                <><IconAlertTriangle size={20} /> Click Again to Confirm Delete</>
              ) : (
                <><IconTrash size={20} /> Mark for Deletion</>
              )}
            </button>
            <p className="text-xs text-white/60 max-w-xs">
              Queue all spam conversations for deletion. The worker will permanently delete them from your CRM within 30 seconds. <strong className="text-orange-300">This cannot be undone!</strong>
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <a
              href={`/settings?location_id=${locationId}`}
              className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium transition-colors inline-flex items-center gap-2"
            >
              <IconSettings size={20} />
              Settings
            </a>
            <p className="text-xs text-white/60 max-w-xs">
              Configure spam detection rules, custom instructions, and business-specific filters
            </p>
          </div>
        </div>
      </div>

      {/* Training Dialog */}
      {trainingDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-lg max-w-md w-full p-6 shadow-xl">
            <h3 className="text-lg font-bold text-white mb-2">🧠 Train AI</h3>
            <p className="text-sm text-white/80 mb-4">
              How would you like to classify this email?
            </p>

            {/* Email Preview */}
            <div className="bg-gray-800/50 rounded p-3 mb-4 max-h-32 overflow-y-auto">
              <p className="text-xs font-semibold text-white/70">From: {trainingDialog.spam_data.sender_email}</p>
              <p className="text-xs text-white/70 mt-1">Subject: {trainingDialog.spam_data.subject}</p>
              <p className="text-xs text-white/60 mt-2 line-clamp-3">{trainingDialog.spam_data.body_preview}</p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={async () => {
                  try {
                    // 1. Mark as not spam (false positive correction)
                    await fetch(`/api/spam/${locationId}/mark-not-spam`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ conversation_id: trainingDialog.conversation_id })
                    });

                    // 2. Add as training example (not spam)
                    await fetch(`/api/spam-rules/${locationId}/training-example`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        conversation_id: trainingDialog.conversation_id,
                        sender_email: trainingDialog.spam_data.sender_email,
                        sender_name: trainingDialog.spam_data.sender_name,
                        subject: trainingDialog.spam_data.subject,
                        body_preview: trainingDialog.spam_data.body_preview,
                        is_spam: false,
                        confidence: 1.0 - trainingDialog.spam_data.confidence
                      })
                    });

                    showAlert('✅ Marked as not spam and added to training examples!', 'success');
                    setRecentSpam(prev => prev.filter(s => s.conversation_id !== trainingDialog.conversation_id));
                    setTrainingDialog(null);
                    refreshDashboardData();
                  } catch (err) {
                    showAlert(`Failed: ${err.message}`, 'error');
                  }
                }}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                ✓ Not Spam
              </button>

              <button
                onClick={async () => {
                  try {
                    // Add as training example (is spam)
                    await fetch(`/api/spam-rules/${locationId}/training-example`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        conversation_id: trainingDialog.conversation_id,
                        sender_email: trainingDialog.spam_data.sender_email,
                        sender_name: trainingDialog.spam_data.sender_name,
                        subject: trainingDialog.spam_data.subject,
                        body_preview: trainingDialog.spam_data.body_preview,
                        is_spam: true,
                        confidence: trainingDialog.spam_data.confidence
                      })
                    });

                    showAlert('✅ Confirmed as spam and added to training examples!', 'success');
                    setTrainingDialog(null);
                    refreshDashboardData();
                  } catch (err) {
                    showAlert(`Failed: ${err.message}`, 'error');
                  }
                }}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                ✗ Is Spam
              </button>

              <button
                onClick={() => setTrainingDialog(null)}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
